/**
 * Base Feature Config Types
 *
 * Base interface for all feature configurations.
 * All feature-specific configs MUST extend this interface.
 *
 * Two-level enabled system:
 * - `enabled` (scope-level): Controls whether this scope's overrides are applied
 *   in the cascade. If false, the scope is skipped. Cascades like other fields.
 * - `active` (in config): The actual on/off switch for the feature. This is what
 *   feature code reads to determine if the feature should run.
 *
 * This separation allows:
 * - Scopes to opt out of the cascade entirely (enabled: false)
 * - Feature on/off state to cascade normally (active in config)
 */

import { z } from "zod";
import type { ConfigScope } from "./config-scope.schemas.js";

// Feature configs derive from this with `.extend({})` even when they add no
// fields: `export const XConfigSchema = BaseFeatureConfigSchema` is an alias
// re-export, which `planta-v2/no-reexports` reports.
export const BaseFeatureConfigSchema = z.object({
  active: z.boolean(),
});

/**
 * Base type for all feature configurations.
 * All feature-specific configs must extend this.
 *
 * Contains the `active` field which determines if the feature is on/off.
 * Feature code should check `config.active` to determine if the feature runs.
 */
export type BaseFeatureConfig = z.infer<typeof BaseFeatureConfigSchema>;

/**
 * Validate a value against a Zod schema and return a ConfigValidationResult.
 * Extracts the first issue's path and message for a human-readable error string.
 *
 * Called by the feature-service against the per-feature schema from the registry:
 * ```ts
 * validateConfigWithSchema(getFeatureDefinitionEntry(key).schema, config);
 * ```
 */
export function validateConfigWithSchema(
  schema: z.ZodType,
  value: BaseFeatureConfig,
): { readonly success: boolean; readonly error?: string } {
  const result = schema.safeParse(value);
  if (result.success === true) {
    return { success: true };
  }
  const firstIssue = result.error.issues[0] ?? {
    path: [],
    message: "Unknown validation error",
  };
  const path = firstIssue.path.join(".");
  return { success: false, error: `${path}: ${firstIssue.message}` };
}

export interface FeatureConfigWithEnabled<
  T extends BaseFeatureConfig = BaseFeatureConfig,
> {
  readonly enabled: boolean;

  readonly config: T;
}

export interface FeatureConfigSources<
  T extends BaseFeatureConfig = BaseFeatureConfig,
> {
  readonly enabled: ConfigScope;
  readonly config: {
    readonly [K in keyof T]: ConfigScope;
  };
}

export interface FeatureConfigWithEnabledAndSources<
  T extends BaseFeatureConfig = BaseFeatureConfig,
> extends FeatureConfigWithEnabled<T> {
  readonly sources: FeatureConfigSources<T>;
}
